import React, { useState } from 'react';
import {
  Box, Typography, Stack, IconButton, LinearProgress, Avatar, Chip, Button,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import PhoneFrame, { AutomatrixBanner, NavBar } from './PhoneFrame';
import { STUDENT } from '../sharedMockData';

/**
 * User just tapped the Khan Academy tile. Shows the three stages of Layer 3
 * injection (§1a): preparing → injecting (invisible in reality; visible here
 * so reviewers can see the concept) → WebView-wrapped Khan landed in.
 *
 * A segmented selector at the top lets reviewers toggle between the three states.
 */

const STAGES = [
  { key: 'preparing', label: '1. Preparing', desc: 'Fetching one-shot login snippet' },
  { key: 'injecting', label: '2. Auto-login', desc: 'WebView.evaluateJavascript runs' },
  { key: 'loaded', label: '3. WebView', desc: 'User is in Khan, logged in' },
];

const StageSwitcher = ({ stage, setStage }) => (
  <Box sx={{
    display: 'flex', gap: 0.5, p: 0.5, mx: 2, mt: 1.5,
    bgcolor: '#eef0f5', borderRadius: 20, flexShrink: 0,
  }}>
    {STAGES.map(s => (
      <Box
        key={s.key}
        onClick={() => setStage(s.key)}
        sx={{
          flex: 1, textAlign: 'center', py: 0.6, px: 0.5,
          borderRadius: 16, cursor: 'pointer',
          bgcolor: stage === s.key ? '#fff' : 'transparent',
          boxShadow: stage === s.key ? '0 1px 3px rgba(16,24,40,0.12)' : 'none',
        }}
      >
        <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: stage === s.key ? '#1565c0' : '#6b6f76' }}>
          {s.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const PreparingStage = () => (
  <Box sx={{
    flexGrow: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', px: 3, gap: 2,
    bgcolor: '#f6f7fb',
  }}>
    <Avatar
      src="https://storage.googleapis.com/nucleos-frontend-assets/icons/application-integrations/KhanAcademy.png"
      variant="rounded"
      sx={{ width: 72, height: 72, bgcolor: '#fff', borderRadius: 3, p: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
    />
    <Typography variant="h6" fontWeight={700} sx={{ textAlign: 'center' }}>
      Preparing Khan Academy…
    </Typography>
    <Typography variant="caption" sx={{ color: '#6b6f76', textAlign: 'center', maxWidth: 240 }}>
      Fetching one-time login snippet from AchieveDXP backend
    </Typography>

    <Box sx={{ width: 220, mt: 1 }}>
      <LinearProgress sx={{ borderRadius: 3, height: 4 }} />
    </Box>

    <Stack spacing={0.5} sx={{ mt: 2, fontSize: '0.7rem', color: '#8a8d93', alignItems: 'center' }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <CheckIcon sx={{ fontSize: 13, color: '#4caf50' }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Authorized · session token valid</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <CheckIcon sx={{ fontSize: 13, color: '#4caf50' }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Credentials resolved (vault bridge)</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%', border: '2px solid #ffc107',
          borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite',
          '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
        }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Awaiting /api/mobile/inject-login</Typography>
      </Stack>
    </Stack>
  </Box>
);

const InjectingStage = () => (
  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#0c1b33', position: 'relative' }}>
    {/* Fake partner login page being filled */}
    <Box sx={{ px: 3, pt: 3, pb: 2, textAlign: 'center' }}>
      <Typography variant="caption" sx={{ color: '#7dd3fc', fontSize: '0.65rem', letterSpacing: '0.1em', fontWeight: 700 }}>
        WEBVIEW.EVALUATEJAVASCRIPT()
      </Typography>
      <Typography variant="subtitle2" sx={{ color: '#fff', mt: 0.5, fontSize: '0.85rem' }}>
        Auto-logging in to Khan Academy…
      </Typography>
    </Box>

    <Box sx={{ mx: 2, mb: 2, p: 2, bgcolor: '#fff', borderRadius: 3 }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#1a1a2e', fontWeight: 700, mb: 1.5 }}>
        Sign in to Khan Academy
      </Typography>
      <Box sx={{
        border: '2px solid #42a5f5', borderRadius: 1, p: 1, mb: 1.25,
        boxShadow: '0 0 0 3px rgba(66,165,245,0.15)',
        position: 'relative',
      }}>
        <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#1a1a2e' }}>
          {STUDENT.khanEmail}|
        </Typography>
        <Box sx={{
          position: 'absolute', top: -18, left: 0, bgcolor: '#42a5f5',
          color: '#fff', fontSize: '0.55rem', px: 0.75, py: 0.15, borderRadius: '4px 4px 0 0', fontFamily: 'monospace',
        }}>
          filling #user
        </Box>
      </Box>
      <Box sx={{
        border: '2px dashed #bdbdbd', borderRadius: 1, p: 1, mb: 1.25,
      }}>
        <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#9e9e9e' }}>
          ••••••••••
        </Typography>
      </Box>
      <Box sx={{ bgcolor: '#14bf96', borderRadius: 20, py: 0.75, textAlign: 'center' }}>
        <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>
          Continue
        </Typography>
      </Box>
    </Box>

    {/* Debug strip — invisible in reality, shown for review legibility */}
    <Box sx={{ mx: 2, mb: 1.5, p: 1.25, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, fontFamily: 'monospace' }}>
      <Typography sx={{ fontSize: '0.6rem', color: '#ffd166', mb: 0.5 }}>
        ⚠ DEV OVERLAY — invisible in production
      </Typography>
      <Typography sx={{ fontSize: '0.6rem', color: '#a5d6a7', lineHeight: 1.6 }}>
        {'> POST /api/mobile/inject-login'}<br />
        {'< 200 OK snippetId=inj_01JABC…'}<br />
        {'> evaluateJavascript(snippet) → '}<span style={{color:'#7dd3fc'}}>submitted</span><br />
        {'> AutomatrixBridge.reportLoginStatus(submitted)'}<br />
        {'> snippet GC\'d · sessionId invalidated'}
      </Typography>
    </Box>

    <Box sx={{ px: 2, pb: 2 }}>
      <LinearProgress sx={{ borderRadius: 3, height: 3, bgcolor: 'rgba(255,255,255,0.15)' }} />
      <Typography sx={{ fontSize: '0.65rem', color: '#bbdefb', mt: 0.75, textAlign: 'center' }}>
        Waiting for [data-testid=home] to appear…
      </Typography>
    </Box>
  </Box>
);

const LoadedStage = () => (
  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', overflow: 'auto' }}>
    {/* Faux Khan top bar */}
    <Box sx={{
      bgcolor: '#14bf96', color: '#fff', px: 2, py: 1.25,
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', letterSpacing: '-0.02em' }}>
        Khan Academy
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#0f8a6a' }}>{STUDENT.initials}</Avatar>
    </Box>

    {/* Welcome + courses */}
    <Box sx={{ p: 2 }}>
      <Chip
        size="small"
        icon={<CheckIcon sx={{ fontSize: 14 }} />}
        label={`Signed in as ${STUDENT.khanUser}`}
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem', mb: 1.5 }}
      />
      <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
        Welcome back, {STUDENT.full.split(' ')[0]}!
      </Typography>
      <Typography variant="caption" sx={{ color: '#5f6368', fontSize: '0.72rem' }}>
        Pick up where you left off
      </Typography>

      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {[
          { title: 'Algebra 1 · Unit 4', pct: 72, color: '#1976d2' },
          { title: 'Reading · 8th grade', pct: 45, color: '#7e57c2' },
          { title: 'World History', pct: 88, color: '#ef6c00' },
        ].map(c => (
          <Box key={c.title} sx={{
            border: '1px solid #e6e8ef', borderRadius: 3, p: 1.5,
          }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{c.title}</Typography>
            <Box sx={{ mt: 0.75, height: 4, bgcolor: '#eef0f5', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ width: `${c.pct}%`, height: '100%', bgcolor: c.color }} />
            </Box>
            <Typography sx={{ fontSize: '0.62rem', color: '#6b6f76', mt: 0.5 }}>{c.pct}% complete</Typography>
          </Box>
        ))}
      </Stack>
    </Box>

    {/* Back overlay */}
    <Box sx={{
      position: 'absolute', bottom: 52, left: 0, right: 0,
      bgcolor: 'rgba(17,18,22,0.88)', color: '#fff',
      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
      borderTopLeftRadius: 16, borderTopRightRadius: 16,
    }}>
      <IconButton size="small" sx={{ color: '#fff' }}>
        <BackIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
        Back to AchieveDXP
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Button size="small" variant="text" sx={{ color: '#42a5f5', textTransform: 'none', fontSize: '0.7rem' }}>
        Close session
      </Button>
    </Box>
  </Box>
);

const AndroidPartnerLaunchMockup = () => {
  const [stage, setStage] = useState('injecting');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 2 }}>
      <PhoneFrame background="#fff" statusDark={stage === 'injecting'}>
        <AutomatrixBanner />

        {/* WebView chrome strip */}
        <Box sx={{
          bgcolor: '#eceff5', px: 1.5, py: 0.75,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <IconButton size="small" sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
          <Box sx={{
            flexGrow: 1, bgcolor: '#fff', borderRadius: 16, px: 1.25, py: 0.5,
            display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #dde0e7',
          }}>
            <LockIcon sx={{ fontSize: 11, color: '#2e7d32' }} />
            <Typography sx={{ fontSize: '0.63rem', fontFamily: 'monospace', color: '#5f6368' }}>
              {stage === 'loaded' ? 'khanacademy.org' : 'khanacademy.org/login'}
            </Typography>
          </Box>
          <IconButton size="small" sx={{ p: 0.25 }}><MoreIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>

        <StageSwitcher stage={stage} setStage={setStage} />

        {stage === 'preparing' && <PreparingStage />}
        {stage === 'injecting' && <InjectingStage />}
        {stage === 'loaded' && <LoadedStage />}

        <NavBar label={`Khan Academy · Layer 3 (${stage})`} dark={stage === 'injecting'} />
      </PhoneFrame>
    </Box>
  );
};

export default AndroidPartnerLaunchMockup;
